/**
 * Core simulation types. Everything here is integer-only (see fixed.ts) so that
 * a battle resolves identically on the server, in the client worker, and in CI.
 *
 * The unit, tower and tile identifiers below are the exact ids produced by
 * @empire/balance-tools from 02_Balance.xlsx. If you add a unit to the
 * spreadsheet, add it here too — the data-integrity test will fail until you do.
 */

export type Tick = number;
/** Sub-tile position in milli-tiles: 1000 = one tile. */
export type MilliTile = number;

/** Road tile types the defender may place (02 GDD §6.2). */
export type TileKind = "empty" | "road" | "mud" | "barricade" | "tower_slot" | "core";

/** The seven trainable units (02_Balance.xlsx :: Units). */
export type UnitKind =
  | "militia"
  | "shieldbearer"
  | "rider"
  | "ram"
  | "priest"
  | "saboteur"
  | "scout";

/** The five towers (02_Balance.xlsx :: Towers). */
export type TowerKind = "arrow" | "ballista" | "brazier" | "frost" | "cannon";

export const UNIT_KINDS: readonly UnitKind[] = [
  "militia",
  "shieldbearer",
  "rider",
  "ram",
  "priest",
  "saboteur",
  "scout",
] as const;

export const TOWER_KINDS: readonly TowerKind[] = [
  "arrow",
  "ballista",
  "brazier",
  "frost",
  "cannon",
] as const;

/**
 * Where a unit is in its life. Declared once here because towers.ts, damage.ts,
 * abilities.ts and simulate.ts all need it, and three copies of a union is
 * three chances for them to disagree.
 */
export type UnitStatus = "waiting" | "walking" | "blocked" | "reached" | "dead" | "retreated";

export interface Coord {
  readonly x: number;
  readonly y: number;
}

/** A tile as placed by the defender (02 GDD §6.2, S07 editor). */
export interface Tile {
  readonly kind: TileKind;
  /** Present only when kind === "tower_slot" and a tower is built. */
  readonly tower?: TowerPlacement;
}

export interface TowerPlacement {
  readonly kind: TowerKind;
  /** 1..3. Level 2 unlocks at Engineer's Guild 5, level 3 at 12 (02 GDD §6.3). */
  readonly level: number;
}

/** The defender's committed layout for a battle. */
export interface Layout {
  readonly size: number;
  /** Row-major, length size*size. */
  readonly tiles: readonly Tile[];
  /** Board-edge tile where attackers enter. */
  readonly gate: Coord;
  /** Drives the road/mud/barricade budgets and the tower HP bonus. */
  readonly wallLevel: number;
  /** Drives how many tower slots there are, and which towers are unlocked. */
  readonly engineersGuildLevel: number;
}

/** One attacking wave: units enter in this order at this tick offset. */
export interface Wave {
  readonly offsetTicks: Tick;
  readonly units: readonly UnitKind[];
}

/** The attacker's committed plan (S11 planner). */
export interface AttackPlan {
  readonly waves: readonly Wave[];
}

export interface BattleInput {
  readonly simVersion: string;
  readonly layout: Layout;
  readonly plan: AttackPlan;
}

/** A raid succeeds if at least one unit reaches the core (02 GDD §5). */
export type BattleOutcome = "breach" | "hold";

/** Compact, replayable event stream. Rendered by the client, never re-simulated. */
export interface BattleEvent {
  readonly tick: Tick;
  readonly type: string;
  readonly a?: number;
  readonly b?: number;
  readonly c?: number;
}

export interface BattleResult {
  readonly simVersion: string;
  readonly outcome: BattleOutcome;
  readonly ticks: Tick;
  /** What became of the attacker's army. */
  readonly army: {
    readonly sent: number;
    readonly reached: number;
    readonly lost: number;
    readonly survived: number;
  };
  /**
   * Which units walk home, by kind — everything that did not die, whether it
   * reached the core or retreated when the clock ran out.
   *
   * Deliberately OUTSIDE the hashed summary (see `hash` below). It is a
   * projection of the same unit statuses `army` already counts, so it adds no
   * determinism coverage worth re-recording twenty goldens and invalidating
   * every stored replay for: the rules did not change when this was added, only
   * what the server is told about them. The server needs it because an attack
   * now spends real units and has to know which ones come back (D-109).
   */
  readonly returning: Readonly<Partial<Record<UnitKind, number>>>;
  /**
   * Which units died, by kind — the bill for the attack. Same reasoning as
   * `returning`, and the two of them add up to what was sent.
   */
  readonly losses: Readonly<Partial<Record<UnitKind, number>>>;
  /** Total Carry of the units that reached the core; the server caps it. */
  readonly carryReaching: number;
  /** What the attacker may see of the layout (02 GDD §7.3). */
  readonly reveal: {
    readonly roadTilesWalked: number;
    readonly towerIds: readonly number[];
  };
  /**
   * Optional, for verification and analytics only. The replay itself is the
   * INPUT plus the version tag — the client re-simulates (02 GDD §8).
   */
  readonly events: readonly BattleEvent[];
  /** Determinism fingerprint. Compared in CI. */
  readonly hash: string;
}

export interface ValidationError {
  readonly code: string;
  readonly message: string;
  readonly tiles: readonly Coord[];
}

export type ValidationResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly error: ValidationError };

// ---------------------------------------------------------------------------
// Shapes of the generated balance tables. These describe data/*.json so that a
// mismatch between the spreadsheet and the code is a compile error, not a
// surprise at runtime.
// ---------------------------------------------------------------------------

export interface ResourceCost {
  readonly wood: number;
  readonly clay: number;
  readonly iron: number;
  readonly grain?: number;
}

export interface UnitStats {
  readonly id: string;
  readonly name: string;
  readonly role: string;
  readonly hp: number;
  readonly armor: number;
  readonly speedMilliPerTick: number;
  readonly carry: number;
  readonly cost: ResourceCost;
  readonly totalCost: number;
  readonly trainTimeSeconds: number;
  readonly upkeepGrainPerHour: number;
  readonly mapSpeedTilesPerHour: number;
  readonly trainedAt: string;
  /**
   * The same rule, parsed: which building trains this unit, and from what level.
   *
   * The prose column stays for display; the server enforces this one. A rule
   * that exists only inside a sentence cannot be checked (D-106, D-107).
   */
  readonly trainedIn: { readonly building: string; readonly level: number };
  readonly special: string | null;
  /** Ram only: damage to towers and barricades in reach. */
  readonly siege?: { readonly damagePerTick: number; readonly reachMilli: number };
  /** Priest only: pulsed so the rate stays whole-number (D-085). */
  readonly heal?: {
    readonly amount: number;
    readonly intervalTicks: number;
    readonly reachMilli: number;
  };
  /** Saboteur only. */
  readonly disable?: {
    readonly reachMilli: number;
    readonly durationTicks: number;
    readonly cooldownTicks: number;
  };
  /** Scout only: takes no part in combat. */
  readonly combat?: boolean;
}

export interface TowerLevelStats {
  readonly damage: number;
  readonly hp: number;
  readonly cooldownTicks: number;
}

export interface TowerStats {
  readonly id: string;
  readonly name: string;
  readonly role: string;
  readonly rangeMilli: number;
  readonly minRangeMilli: number;
  readonly splashRadiusMilli: number;
  readonly piercesArmor: boolean;
  readonly effect: string | null;
  readonly cost: ResourceCost;
  readonly unlockEngineersGuild: number;
  readonly levels: readonly TowerLevelStats[];
  /** Frost only. speedPermille is the resulting speed, not the reduction. */
  readonly slow?: { readonly speedPermille: number; readonly durationTicks: number };
}

export interface TileStats {
  readonly id: string;
  readonly name: string;
  readonly speedPermille: number;
  readonly hp?: number;
  readonly hpPerWallLevelPermille?: number;
  readonly unitDamagePerTick?: number;
}

export interface DefenceBudgetRow {
  readonly wallLevel: number;
  readonly roadTiles: number;
  readonly mudTiles: number;
  readonly barricades: number;
  readonly towerHpBonusPermille: number;
}
